/**
 * Error Boundary pour les widgets Brydge
 *
 * Capture les erreurs React non gérées et affiche une interface
 * de récupération avec possibilité de réessayer.
 *
 * @module components/WidgetErrorBoundary
 */

import React from 'react';
import PropTypes from 'prop-types';
import {Box, Typography, Button} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {logger} from '../../../utils/logger';

/**
 * Composant Error Boundary pour capturer les erreurs React
 *
 * @class WidgetErrorBoundary
 * @extends React.Component
 */
class WidgetErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {hasError: false, error: null};
  }

  static getDerivedStateFromError(error) {
    return {hasError: true, error};
  }

  componentDidCatch(error, errorInfo) {
    // Journalisation de l'erreur sans informations sensibles
    logger.error('Widget Error:', {
      message: error.message,
      component: this.props.widgetType,
      timestamp: new Date().toISOString(),
    });
  }

  handleRetry = () => {
    this.setState({hasError: false, error: null});
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 400,
            p: 3,
            textAlign: 'center',
          }}
        >
          <ErrorOutlineIcon
            sx={{fontSize: 48, color: 'error.main', mb: 2}}
            aria-hidden="true"
          />
          <Typography variant="h6" component="h2" gutterBottom>
            Une erreur est survenue
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
            Le widget n&apos;a pas pu se charger correctement.
          </Typography>
          <Button
            variant="outlined"
            onClick={this.handleRetry}
            size="small"
            aria-label="Réessayer de charger le widget"
          >
            Réessayer
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

WidgetErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  widgetType: PropTypes.string.isRequired,
  onRetry: PropTypes.func,
};

export default WidgetErrorBoundary;
